/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: components\SipProfileSettingsTabs.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the SIP Profiles module.
----------------------------------------------------------------*/


import React from 'react';
import { Settings as SettingsIcon, RotateCcw } from 'lucide-react';
import Toggle from '../../../components/ui/Toggle';
import { CodecField } from '../../../components/ui/CodecField';
import { SimpleTooltip } from '../../../components/ui/SimpleTooltip';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../../components/ui/tabs';
import { getParameterConfig, getParameterLabel, getParameterTooltip } from '../sipParametersConfig';
import type { SipProfileSetting } from '../types';

interface Category {
    id: number;
    name: string;
    description?: string;
}

interface SipProfileSettingsTabsProps {
    settings: SipProfileSetting[];
    filteredCategories: Category[];
    activeTab: string | null;
    onTabChange: (id: string) => void;
    searchResults: Record<number, number>;
    searchQuery: string;
    isReadonly: boolean;
    getCategoryName: (name: string) => string;
    onSettingChange: (index: number, value: string) => void;
    onResetToDefault: (index: number) => void;
    t: (key: string) => string;
}

export function SipProfileSettingsTabs({
    settings, filteredCategories, activeTab, onTabChange,
    searchResults, searchQuery, isReadonly, getCategoryName,
    onSettingChange, onResetToDefault, t,
}: SipProfileSettingsTabsProps) {
    if (settings.length === 0 || activeTab === null) return null;

    const getSettingsForCategory = (categoryId: string): SipProfileSetting[] => {
        const category = filteredCategories.find((c) => String(c.id) === categoryId);
        const categorySettings = settings
            .filter((s) => String(s.categoryId) === categoryId || (category && s.category === category.name))
            .sort((a, b) => (a.settingOrder || 0) - (b.settingOrder || 0));

        if (!searchQuery.trim()) return categorySettings;

        const query = searchQuery.toLowerCase();
        return categorySettings.filter((setting) => {
            const name = setting.name?.toLowerCase() || '';
            const value = setting.value?.toString().toLowerCase() || '';
            const label = getParameterLabel(setting.name, t)?.toLowerCase() || '';
            const tooltip = getParameterTooltip(setting.name, t)?.toLowerCase() || '';
            return name.includes(query) || value.includes(query) || label.includes(query) || tooltip.includes(query);
        });
    };

    return (
        <div className="bg-card border border-border rounded-lg p-4">
            <Tabs value={activeTab ?? ''} onValueChange={(val) => onTabChange(val)}>
                <TabsList className="w-full justify-start overflow-x-auto flex-wrap">
                    {filteredCategories.map((category) => {
                        const hasResults = searchQuery.trim() && searchResults[category.id] > 0;
                        const resultCount = searchResults[category.id] || 0;
                        return (
                            <TabsTrigger
                                key={category.id}
                                value={String(category.id)}
                                icon={<SettingsIcon className="w-4 h-4" />}
                                className={hasResults ? 'relative' : ''}
                            >
                                <span className="flex items-center gap-2">
                                    {getCategoryName(category.name)}
                                    {hasResults && (
                                        <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-[10px] font-bold text-white bg-green-500 rounded-full">
                                            {resultCount}
                                        </span>
                                    )}
                                </span>
                            </TabsTrigger>
                        );
                    })}
                </TabsList>

                {filteredCategories.map((category) => {
                    const categorySettings = getSettingsForCategory(String(category.id));
                    return (
                        <TabsContent key={category.id} value={String(category.id)}>
                            {category.description && (
                                <div className="text-sm text-muted-foreground bg-muted/30 p-3 rounded-lg mb-4">
                                    {category.description}
                                </div>
                            )}

                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-start">
                                {categorySettings.map((setting, index) => {
                                    const settingIndex = settings.indexOf(setting);
                                    const isOdd = index % 2 !== 0;
                                    const paramConfig = getParameterConfig(setting.name);
                                    const label = getParameterLabel(setting.name, t) || setting.description || setting.name;
                                    const tooltip = getParameterTooltip(setting.name, t) || setting.description || '';
                                    const fieldType = paramConfig?.fieldType || 'text';
                                    const possibleValues = paramConfig?.possibleValues || [];

                                    const isBoolean = setting.value === 'true' || setting.value === 'false' || 
                                        (possibleValues.length === 2 && possibleValues.includes('true') && possibleValues.includes('false')) ||
                                        (possibleValues.length === 2 && possibleValues.includes('yes') && possibleValues.includes('no'));

                                    return (
                                        <React.Fragment key={`setting-${setting.name}-${index}`}>
                                            <SimpleTooltip content={tooltip || label} position="top" maxWidth="400px">
                                                <div className="flex flex-col">
                                                    <label className="text-sm font-medium text-foreground">{label}</label>
                                                    <span className="text-xs text-muted-foreground mt-0.5">{setting.name}</span>
                                                </div>
                                            </SimpleTooltip>

                                            <div className="flex items-center gap-2 sm:mb-0 mb-4">
                                                {isBoolean ? (
                                                    <Toggle
                                                        value={setting.value === 'true' || setting.value === 'yes'}
                                                        onChange={(checked) => {
                                                            const isYesNo = possibleValues.includes('yes') || setting.value === 'yes' || setting.value === 'no';
                                                            onSettingChange(settingIndex, checked ? (isYesNo ? 'yes' : 'true') : (isYesNo ? 'no' : 'false'));
                                                        }}
                                                        disabled={isReadonly}
                                                        trueLabel={t('common.yes')}
                                                        falseLabel={t('common.no')}
                                                    />
                                                ) : fieldType === 'select' && possibleValues.length > 0 ? (
                                                    <select
                                                        value={setting.value}
                                                        onChange={(e) => onSettingChange(settingIndex, e.target.value)}
                                                        disabled={isReadonly}
                                                        className={`flex-1 px-3 py-2 text-sm border border-border rounded bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-primary ${isReadonly ? 'opacity-70 cursor-not-allowed' : ''}`}
                                                        aria-label={`${t('sipProfiles.defaultValue')}: ${setting.defaultValue}`}
                                                    >
                                                        {possibleValues.map((val) => (
                                                            <option key={val} value={val}>{val}</option>
                                                        ))}
                                                    </select>
                                                ) : fieldType === 'number' ? (
                                                    <input
                                                        type="number"
                                                        value={setting.value}
                                                        onChange={(e) => onSettingChange(settingIndex, e.target.value)}
                                                        disabled={isReadonly}
                                                        className={`flex-1 px-3 py-2 text-sm border border-border rounded bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary ${isReadonly ? 'opacity-70 cursor-not-allowed' : ''}`}
                                                        placeholder={setting.defaultValue}
                                                        aria-label={`${t('sipProfiles.defaultValue')}: ${setting.defaultValue}`}
                                                    />
                                                ) : fieldType === 'codec' ? (
                                                    <CodecField
                                                        value={setting.value}
                                                        onChange={(value) => onSettingChange(settingIndex, value)}
                                                        disabled={isReadonly}
                                                        placeholder={setting.defaultValue}
                                                    />
                                                ) : (
                                                    <input
                                                        type="text"
                                                        value={setting.value}
                                                        onChange={(e) => onSettingChange(settingIndex, e.target.value)}
                                                        disabled={isReadonly}
                                                        className={`flex-1 px-3 py-2 text-sm border border-border rounded bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary ${isReadonly ? 'opacity-70 cursor-not-allowed' : ''}`}
                                                        placeholder={setting.defaultValue}
                                                        aria-label={`${t('sipProfiles.defaultValue')}: ${setting.defaultValue}`}
                                                    />
                                                )}

                                                {!isReadonly && (
                                                    <SimpleTooltip
                                                        content={setting.value !== setting.defaultValue ? t('sipProfiles.resetToDefault') : t('sipProfiles.defaultValueActive')}
                                                        position="top"
                                                    >
                                                        <button
                                                            type="button"
                                                            onClick={() => onResetToDefault(settingIndex)}
                                                            disabled={setting.value === setting.defaultValue}
                                                            className={`p-1.5 rounded transition-colors ${setting.value !== setting.defaultValue
                                                                ? 'text-orange-500 hover:text-orange-600 hover:bg-orange-50 dark:hover:bg-orange-950'
                                                                : 'text-muted-foreground/30 cursor-not-allowed'}`}
                                                            aria-label={setting.value !== setting.defaultValue
                                                                ? `${t('sipProfiles.defaultValue')}: ${setting.defaultValue}`
                                                                : t('sipProfiles.defaultValueActive')}
                                                        >
                                                            <RotateCcw className="h-4 w-4" />
                                                        </button>
                                                    </SimpleTooltip>
                                                )}
                                            </div>

                                            {isOdd && index === categorySettings.length - 1 && (
                                                <>
                                                    <div className="hidden lg:block"></div>
                                                    <div className="hidden lg:block"></div>
                                                </>
                                            )}
                                        </React.Fragment>
                                    );
                                })}
                            </div>

                            {categorySettings.length === 0 && (
                                <div className="text-center text-muted-foreground py-8">
                                    {t('sipProfiles.noSettings')}
                                </div>
                            )}
                        </TabsContent>
                    );
                })}
            </Tabs>
        </div>
    );
}
